export type LeaseDisplayType =
  | 'lejlighed' | 'enkeltværelse' | 'ejerlejlighed' | 'andelsbolig'
  | 'hus' | 'villa' | 'rækkehus' | 'andet'

export type UtilityProvider =
  | 'udlejer' | 'lejer' | 'andelshave'
  | 'fjernvarme' | 'naturgas' | 'el' | 'olie' | 'andet' | ''

export interface Lease {
  id: string
  unit_no: string
  property_id: string
  type: string
  usage_type?: string
  area_m2: number | null
  room_count: number | null
  floor: string | null
  energy_label: string | null
  beboerrepraesentation: boolean
  property_address: string | null
  property_city: string | null
  // Per-forbrugstype availability — is the lejemål tilkoblet a forbrugsgruppe of
  // each type on the property? Derived by GET /leases/{id}; the list endpoint
  // returns it empty ({} → all-false). Drives the erhverv wizard Forbrug-step
  // greying of the "Aconto til udlejer" / "Inkluderet i lejen" radios.
  forbrug_availability?: { el?: boolean; vand?: boolean; varme?: boolean; koeling?: boolean }
}

export interface Renter {
  id: string
  name: string
  email: string
  phone: string | null
  party_kind?: string
  cvr_nr?: string | null
  // F5 — selskab e-faktura routing (EAN-nummer + faktura-email).
  ean_number?: string | null
  invoice_email?: string | null
  // Erhverv Lejer-step nuances (C1).
  prefers_letter?: boolean
  manual_address?: boolean
  address_street?: string | null
  address_postnr?: string | null
  address_by?: string | null
  address_land?: string | null
  no_email?: boolean
  no_cpr?: boolean
  iban?: string | null
  swift?: string | null
}

// usage_types that route a contract to the erhverv (commercial) flow.
export const COMMERCIAL_USAGE_TYPES = ['erhverv', 'kontor', 'lager', 'andet']

export function isCommercialUsage(usage?: string): boolean {
  return !!usage && COMMERCIAL_USAGE_TYPES.includes(usage)
}

// A3 — the subset of the backend ContractResponse the erhverv wizard hydrates
// from when resuming a draft (?draft=<id>). Field names mirror contracts.py's
// ContractResponse exactly; all optional so a partial/early draft still loads.
export interface ErhvervContract {
  id: string
  lease_id: string
  contract_kind?: string
  type?: string
  last_step?: number
  monthly_rent_oere?: number
  deposit_oere?: number
  prepaid_rent_oere?: number
  start_date?: string | null
  end_date?: string | null
  momspligtig?: boolean
  tidsbegraenset_opsigelig?: boolean | null
  erhverv_formaal?: string | null
  erhverv_saerlige_vilkaar?: string | null
  uopsigelig_lejer_value?: number | null
  uopsigelig_lejer_enhed?: string | null
  uopsigelig_udlejer_value?: number | null
  uopsigelig_udlejer_enhed?: string | null
  opsigelsesvarsel_maaneder?: number | null
  opsigelsesvarsel_enhed?: string | null
  afstaaelsesret?: boolean
  drift_skatter?: string | null
  istandsaettelse_14dage?: boolean
  inspection_date?: string | null
  inspection_time?: string | null
  indflytningssyn_email_reminder?: boolean
  regulering_type?: string
  regulering_pct?: number | null
  regulering_first_date?: string | null
  regulering_trappe?: { ikrafttraeden: string; aarlig_leje_oere: number; procent?: number }[]
  regulering_trappe_type?: string
  markedsleje_target_oere?: number | null
  regulering_indices?: { indekstype: string; indeks_basis_period?: string | null; procent?: number }[]
  regulering_maaned?: string | null
  regulering_foerste_aar?: number | null
  regulering_min_pct?: number | null
  regulering_max_pct?: number | null
  regulering_cap_type?: string
  regulering_min_beloeb_oere?: number | null
  regulering_max_beloeb_oere?: number | null
  regulering_indeks_maaned?: string | null
  regulering_indeks_mere_end_aar?: boolean
  regulering_fast_type?: string
  regulering_fast_beloeb_oere?: number | null
  send_varsling?: boolean
  send_varsling_auto?: boolean
  reguler_depositum?: boolean
  reguler_forudbetalt?: boolean
  leje_lines?: { gl_account?: string | null; tekst?: string | null; amount_oere: number; momspligtig?: boolean; sort?: number; kategori?: string | null; gruppe?: string | null }[]
  opkraevningsinterval?: string
  bankgaranti?: boolean
  betalingsservice_tilmeld?: boolean
  bankgaranti_modtaget?: boolean
  bankgaranti_forventet_dato?: string | null
  bankgaranti_beloeb_oere?: number | null
  bankgaranti_stiller?: string | null
  bankgaranti_stiller_adresse?: string | null
  bankgaranti_stiller_postnr?: string | null
  bankgaranti_stiller_by?: string | null
  bankgaranti_maaneder?: number | null
  bankgaranti_modtaget_dato?: string | null
  bankgaranti_gyldig_til?: string | null
  bankgaranti_note?: string | null
  bankgaranti_document_filename?: string | null
  depositum_maaneder?: number | null
  forudbetalt_maaneder?: number | null
  engangsbeloeb?: { bidragstype?: string | null; tekst?: string | null; beloeb_oere: number; momspligtig?: boolean }[]
  forbrug?: {
    typer?: Record<string, { afregning?: string; aconto_oere?: number | null; fuldmagt?: boolean }>
    internet?: { bidrag?: boolean; aconto_oere?: number | null }
    tv?: { bidrag?: boolean; aconto_oere?: number | null; tv_provider?: string | null }
  }
  indflytningsopkraevning?: boolean
  indflytning_forfald?: string | null
  indflytning_opsplit?: boolean
  indflytning_poster?: { post?: string | null; beloeb_oere: number; forfaldsdato?: string | null }[]
  // Generering (A4-partial) — paragraph include/exclude builder.
  generering?: {
    excluded_sections?: string[]
    extra_paragraphs?: { title: string; content: string }[]
  }
}

// A3 part 2 — erhverv flow templates. The list endpoint returns summaries (no
// payload); GET /{id} returns the full ErhvervContract field-set as `payload`.
export interface TemplateSummary {
  id: string
  name: string
  created_at: string
}
export interface TemplateResponse extends TemplateSummary {
  payload: Partial<ErhvervContract>
}

export interface Company {
  id: string
  name: string
  address: string | null
  cvr: string | null
  email: string | null
  phone: string | null
  postnr: string | null
  by: string | null
  // Per-company kontoplan (migr. 043): { account_no: label }. Feeds the erhverv
  // Leje line-item "Konto" dropdown. Optional — null/absent → free-text konto.
  gl_accounts?: Record<string, string> | null
}

export interface LejerManuel {
  firstname: string
  lastname: string
  email: string
  phone: string
  phone_country: string
  address: string
  address2: string
  postnr: string
  by: string
  land: string
  cpr: string
  reg_nr: string
  konto_nr: string
}

export interface WizardState {
  // Pre-wizard
  lease_id: string
  lease_display_type: LeaseDisplayType | ''
  lease_display_other: string   // free text when boligtype === 'andet' (own field, not access_other)
  property_address: string
  property_city: string
  area_m2: string
  commercial_area_m2: string
  room_count: string
  floor: string
  energy_label: string
  is_sublease: boolean

  // §1 timing
  start_date: string
  end_date: string
  is_fixed_term: boolean

  // §3 rent
  monthly_rent_dkk: string
  payment_day: string
  deposit_dkk: string
  prepaid_rent_dkk: string
  first_payment_date: string
  first_payment_dkk: string

  // §3 a conto
  aconto_heating_dkk: string
  aconto_heating_index_start: string  // "MM-DD" — varmeregnskabsåret starter
  aconto_water_dkk: string
  aconto_water_index_start: string    // "MM-DD" — vandregnskabsåret starter
  aconto_electricity_dkk: string
  aconto_el_start_date: string        // "MM-DD" — startdato for levering af el
  aconto_cooling_dkk: string
  aconto_koling_start_date: string    // "MM-DD" — forsyning køling startdato
  aconto_antenna_dkk: string
  aconto_internet_dkk: string

  // Utility meter flags
  vand_individuelt_maaler: boolean
  koling_individuelt_maaler: boolean

  // §5
  heating_provider: UtilityProvider
  heating_type: string
  heating_type_other: string   // free text when heating_type === 'andet'
  water_provider: UtilityProvider
  electricity_provider: UtilityProvider
  cooling_provider: UtilityProvider

  // §6
  antenna_included: boolean
  antenna_notes: string
  internet_included: boolean
  internet_provider: string

  // §7
  inspection_date: string
  inspection_time: string

  // §8–9
  interior_maintenance: 'lejer' | 'udlejer'
  maintenance_notes: string
  inventory_notes: string

  // §10–11
  pets_allowed: boolean
  pets_notes: string
  beboerrepraesentation: boolean
  nettoprisindeks: boolean
  nettoprisindeks_base_year: string
  free_rent: boolean
  free_rent_reason: string
  // Reguleringsregime for fravalget (kun frontend; sendes ikke til backend). '' = uafklaret.
  // Bruges af Vilkår-trinnet til at foreslå §54-klausul, jf. regimeClauses.ts.
  rent_regime?: '' | 'ureguleret' | 'reguleret'
  // Sidst anvendte regime-kombination + de klausul-id'er der blev auto-tilføjet (kun frontend).
  regime_applied?: { key: string; added: string[]; dismissed: string[] }
  // Landlords egne tekstrettelser af smartvilkår (id → tekst), løftet hit så en regimeændring
  // på Generelt-trinnet kan genopbygge §11 uden at miste dem (kun frontend).
  smart_overrides?: Record<string, string>
  // §11 kan indeholde en forældet auto-klausul efter regimeændring og skal gennemgås (kun frontend).
  special_terms_review?: boolean
  // Landlord har selv redigeret §11-teksten i tekstboksen (kun frontend); så genopbygges den ikke automatisk.
  special_terms_edited?: boolean
  payment_frequency: 'monthly' | 'quarterly'
  regulation_day: string
  regulation_month: string
  regulation_index_from: string   // "1"–"12", reference month from (e.g. "10" = Oktober)
  regulation_index_to: string     // "1"–"12", reference month to
  regulation_first_date: string
  regulation_max_pct: string
  special_terms: string

  // Parter
  renter_id: string
  lejer_mode: 'select' | 'indhent' | 'manuel'
  indhent_email: string
  lejere_manuel: LejerManuel[]
  mitid_signing: boolean
  english_translation: boolean

  // Betaling
  payment_method: 'manuel' | 'betalingsservice' | ''

  // Adgang & brugsret (§1 subsection)
  access_rights: string[]   // e.g. ['kælder', 'loftrum', 'p-plads']
  access_other: string[]    // up to 3 custom brugsret rows when 'andet' is in access_rights
  access_numbers: { [key: string]: string }  // nr. for loftrum / garage

  // Billing type (UI layer — provider fields kept for PDF backend compat)
  heating_billing_type: 'aconto' | 'inkluderet' | 'lejer' | null
  water_billing_type: 'aconto' | 'inkluderet' | 'lejer' | null
  el_billing_type: 'aconto' | 'inkluderet' | 'lejer' | null
  cooling_billing_type: 'aconto' | 'inkluderet' | 'ingen' | null

  // TV/internet
  antenna_type: 'faellesantenne' | 'ingen' | null
  internet_type: 'udlejer' | 'ingen' | null

  // Beboerrepræsentation monthly amount
  beboer_amount_dkk: string

  // Dynamic extra contributions
  extra_contributions: { name: string; dkk: string }[]

  // §5 Øvrigt & bilag
  husorden: 'nej' | 'ja' | 'standard' | 'ejendom' | ''
  other_notes: string          // §10 øvrige oplysninger
  unit_number: string          // lejemålsnummer

  // §11 multi-clause support (joined to special_terms when sending)
  special_terms_clauses: string[]
  smartvilkaar_active: string[]

  // §3 payment dates
  deposit_payment_date: string
  prepaid_payment_date: string

  // Engangsudgifter (one-time costs at move-in)
  engangsudgifter: { name: string; dkk: string }[]

  // §8 vedligeholdelse
  exterior_maintenance: 'lejer' | 'udlejer'

  // §9 inventar (appliance checkboxes)
  appliances: string[]
  appliances_other: string[]   // custom items added via "Tilføj inventar"

  // §10 benyttelse
  smoking_allowed: boolean
  pets_option: 'allowed' | 'not_allowed' | 'dispensation' | ''

  // Flyttesyn
  multiple_units: boolean
  move_in_inspection: boolean
  move_out_inspection: boolean

  // Fields pre-filled from lease/BBR — used for showing "Forududfyldt" indicator
  prefilled_fields: string[]

  // Lejer nuværende adresse (persisted to DB → PDF pg1-13)
  lejer_address: string | null
}

export const INITIAL_WIZARD_STATE: WizardState = {
  lease_id: '', lease_display_type: '', lease_display_other: '', property_address: '', property_city: '',
  area_m2: '', commercial_area_m2: '', room_count: '', floor: '', energy_label: '', is_sublease: false,
  start_date: '', end_date: '', is_fixed_term: false,
  monthly_rent_dkk: '', payment_day: '1', deposit_dkk: '', prepaid_rent_dkk: '',
  first_payment_date: '', first_payment_dkk: '',
  aconto_heating_dkk: '', aconto_heating_index_start: '',
  aconto_water_dkk: '', aconto_water_index_start: '',
  aconto_electricity_dkk: '', aconto_el_start_date: '',
  aconto_cooling_dkk: '', aconto_koling_start_date: '',
  aconto_antenna_dkk: '', aconto_internet_dkk: '',
  vand_individuelt_maaler: false, koling_individuelt_maaler: false,
  heating_provider: '', heating_type: '', heating_type_other: '', water_provider: '',
  electricity_provider: '', cooling_provider: '',
  antenna_included: false, antenna_notes: '', internet_included: false, internet_provider: '',
  inspection_date: '', inspection_time: '',
  interior_maintenance: 'lejer', maintenance_notes: '', inventory_notes: '',
  pets_allowed: false, pets_notes: '', beboerrepraesentation: false,
  nettoprisindeks: false, nettoprisindeks_base_year: '', free_rent: false,
  free_rent_reason: '', rent_regime: '', payment_frequency: 'monthly',
  regulation_day: '1', regulation_month: '1',
  regulation_index_from: '10', regulation_index_to: '10',
  regulation_first_date: '', regulation_max_pct: '',
  special_terms: '',
  renter_id: '',
  lejer_mode: 'select', indhent_email: '', lejere_manuel: [], mitid_signing: true, english_translation: false,
  payment_method: 'manuel',
  access_rights: [], access_other: [], access_numbers: {},
  special_terms_clauses: [''],
  smartvilkaar_active: [],
  prefilled_fields: [],
  heating_billing_type: null, water_billing_type: null,
  el_billing_type: null, cooling_billing_type: null,
  antenna_type: null, internet_type: null,
  beboer_amount_dkk: '', extra_contributions: [],
  husorden: '', other_notes: '', unit_number: '',
  deposit_payment_date: '', prepaid_payment_date: '',
  engangsudgifter: [],
  exterior_maintenance: 'udlejer',
  appliances: [], appliances_other: [],
  smoking_allowed: false,
  pets_option: '',
  multiple_units: false, move_in_inspection: false, move_out_inspection: false,
  lejer_address: null,
}

export const toOere = (dkk: string): number =>
  Math.round(parseFloat(dkk.replace(/\./g, '').replace(',', '.') || '0') * 100)

export interface ValidationError { step: number; stepName: string; label: string }

const STEP_NAMES = [
  'Generelt', 'Forbrug & bidrag', 'Indbetalinger', 'Inventar & vedligehold',
  'Øvrigt & bilag', 'Særlige vilkår §11', 'Udlejer', 'Lejer', 'Gennemse & send',
]

const ALL_REQUIRED: { step: number; key: keyof WizardState; label: string }[] = [
  { step: 0, key: 'lease_display_type', label: 'Boligtype' },
  { step: 0, key: 'area_m2', label: 'Areal' },
  { step: 0, key: 'room_count', label: 'Antal værelser' },
  { step: 0, key: 'monthly_rent_dkk', label: 'Månedlig husleje' },
  { step: 0, key: 'start_date', label: 'Indflytningsdato' },
  { step: 1, key: 'heating_billing_type', label: 'Varme afregning' },
  { step: 1, key: 'water_billing_type', label: 'Vand afregning' },
  { step: 1, key: 'el_billing_type', label: 'El afregning' },
  { step: 2, key: 'deposit_dkk', label: 'Depositum' },
  { step: 2, key: 'prepaid_rent_dkk', label: 'Forudbetalt leje' },
]

function fieldFilled(v: WizardState[keyof WizardState]): boolean {
  if (v === null || v === undefined || v === '' || v === false) return false
  if (Array.isArray(v)) return v.length > 0
  return true
}

export function validateWizard(state: WizardState): ValidationError[] {
  const errors = ALL_REQUIRED
    .filter(r => !fieldFilled(state[r.key]))
    .map(r => ({ step: r.step, stepName: STEP_NAMES[r.step], label: r.label }))

  // Step 0: boligtype "andet" requires a description
  if (state.lease_display_type === 'andet' && !state.lease_display_other.trim()) {
    errors.push({ step: 0, stepName: STEP_NAMES[0], label: 'Beskriv boligtypen' })
  }

  // Step 0: fixed-term lease requires an end date
  if (state.is_fixed_term && !state.end_date) {
    errors.push({ step: 0, stepName: STEP_NAMES[0], label: 'Slutdato' })
  }

  // §11 skal gennemgås, hvis en regimeændring kan have efterladt en forældet klausul
  if (state.special_terms_review) {
    errors.push({ step: 5, stepName: STEP_NAMES[5], label: 'Gennemgå § 11 (regime er ændret)' })
  }

  // Step 7: Lejer — check based on mode
  const lejerOk = (
    (state.lejer_mode === 'select' && !!state.renter_id) ||
    (state.lejer_mode === 'indhent' && !!state.indhent_email &&
      !!state.lejere_manuel[0]?.firstname && !!state.lejere_manuel[0]?.lastname) ||
    (state.lejer_mode === 'manuel' && !!state.lejere_manuel[0]?.firstname && !!state.lejere_manuel[0]?.email)
  )
  if (!lejerOk) {
    errors.push({ step: 7, stepName: STEP_NAMES[7], label: 'Lejer oplysninger' })
  }

  return errors
}
